import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { getStudentPlanMeta } from '../lib/firestore/studentPlan';
import AppLayout from '../components/AppLayout';
import InterventionsPlanDisplay from '../components/InterventionsPlanDisplay';
import toast from 'react-hot-toast';

const InterventionsBuilderPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [student, setStudent] = useState(null);
  const [planMeta, setPlanMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [interventionsPlan, setInterventionsPlan] = useState(null);
  const [generatingPlan, setGeneratingPlan] = useState(false);

  // Generate interventions plan from questionnaire answers
  const generateInterventionsPlan = async () => {
    if (!planMeta?.questionnaireAnswers) {
      toast.error('No questionnaire answers found. Please complete the assessment first.');
      return;
    }

    setGeneratingPlan(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/generateInterventionsPlan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          studentId: id,
          questionnaireAnswers: planMeta.questionnaireAnswers
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate interventions plan');
      }

      const data = await response.json();
      setInterventionsPlan(data.plan);
      toast.success('Interventions plan generated successfully!');
    } catch (error) {
      console.error('Error generating interventions plan:', error);
      toast.error('Failed to generate interventions plan');
    } finally {
      setGeneratingPlan(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch student data
        const studentRef = doc(db, 'students', id);
        const studentSnap = await getDoc(studentRef);
        
        if (!studentSnap.exists()) {
          navigate('/students');
          return;
        }
        
        const studentData = { id: studentSnap.id, ...studentSnap.data() };
        setStudent(studentData);
        
        // Fetch plan metadata
        const meta = await getStudentPlanMeta(id);
        setPlanMeta(meta);
        
        // Auto-generate interventions plan if questionnaire answers exist
        if (meta?.questionnaireAnswers) {
          await generateInterventionsPlan();
        }
        
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Error loading data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  const handleAcceptIntervention = (intervention) => {
    // TODO: Save accepted intervention to database
    toast.success(`Accepted intervention: ${intervention.title}`);
  };

  const handleAcceptGroup = (group) => {
    // TODO: Save accepted group to database
    toast.success(`Accepted group: ${group.suggestedTitle || group.groupId}`);
  };

  const handleRejectSuggestion = (type, id) => {
    // TODO: Log rejection for future reference
    toast.success(`Rejected ${type}: ${id}`);
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </AppLayout>
    );
  }

  if (!student) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Student Not Found</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>The student could not be found in the database.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!planMeta?.questionnaireAnswers) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-yellow-800 mb-2">Assessment Required</h3>
            <p className="text-yellow-700 mb-4">
              This student needs to complete the questionnaire assessment before interventions can be recommended.
            </p>
            <button
              onClick={() => navigate(`/student/${id}/triage`)}
              className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            >
              Start Assessment
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Interventions Plan for {student.firstName} {student.lastName}
              </h1>
              <p className="text-gray-600 mt-1">
                AI-generated intervention recommendations based on assessment results.
              </p>
            </div>
            <div className="flex space-x-3">
              {!interventionsPlan && (
                <button
                  onClick={generateInterventionsPlan}
                  disabled={generatingPlan}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generatingPlan ? 'Generating...' : 'Generate Plan'}
                </button>
              )}
              <button
                onClick={() => navigate(`/student/${id}`)}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Back to Student
              </button>
            </div>
          </div>
        </div>

        {/* Student Info */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Student Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Name</label>
              <p className="text-gray-900">{student.firstName} {student.lastName}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Year Group</label>
              <p className="text-gray-900">{student.yearGroup || 'Not specified'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">NC Status</label>
              <p className="text-gray-900">{planMeta?.ncStatus || 'Not assessed'}</p>
            </div>
          </div>
        </div>

        {/* Interventions Plan Display */}
        {interventionsPlan ? (
          <InterventionsPlanDisplay
            plan={interventionsPlan}
            onAcceptIntervention={handleAcceptIntervention}
            onAcceptGroup={handleAcceptGroup}
            onRejectSuggestion={handleRejectSuggestion}
          />
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <div className="text-center text-gray-500">
              <p>No interventions plan available. Click "Generate Plan" to create recommendations based on the assessment.</p>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default InterventionsBuilderPage;